import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { fetchTopicById, updateTopic } from "../../../apis/CourseApis";
import { useAuth } from "../../Authentication/AuthContext";
import { useToastHelpers } from "../../Toasts/ToastUtils";
import { useTheme } from "../../Theme/ThemeContext";
import { SkeletonFormPage } from "../../Skeletons";
import SeoMeta from "../../SeoMeta";
import EditTheoryTopic from "./edit/EditTheoryTopic";
import EditVideoTopic from "./edit/EditVideoTopic";
import EditMCQTopic from "./edit/EditMCQTopic";
import EditCodingTopic from "./edit/EditCodingTopic";
import { DEFAULT_BOILERPLATE_CODES } from "../../../assets/boilerplateConstants";

export default function EditTopic() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const { successToast, errorToast } = useToastHelpers();

  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Load topic data
  useEffect(() => {
    let isMounted = true;

    const splitGoBlockFromText = (raw) => {
      const text = String(raw ?? "");
      const start = text.indexOf("go:");
      if (start === -1) return { text: text.trim(), code: "" };
      const end = text.indexOf(":go", start + 3);
      if (end === -1) return { text: text.trim(), code: "" };

      const code = text.slice(start + 3, end).replace(/^\n/, "").trimEnd();
      const before = text.slice(0, start).trimEnd();
      const after = text.slice(end + 3).trimStart();
      const cleaned = [before, after].filter(Boolean).join("\n").trim();
      return { text: cleaned, code };
    };

    const loadTopic = async () => {
      setIsLoading(true);
      try {
        const topicData = await fetchTopicById(topicId);
        if (!isMounted) return;

        // Preserve orderNo so editing doesn't reset ordering.
        const preservedOrderNo = topicData?.orderNo;

        // Map backend data to formData based on topic type
        const baseData = {
          title: topicData.title || "",
          points: topicData.points ?? "",
          type: topicData.type,
          chapterId: topicData.chapterId,
          orderNo: preservedOrderNo,
        };

        if (topicData.type === "theory") {
          setFormData({
            ...baseData,
            theoryContent: topicData.theoryContent || [""],
          });
        } else if (topicData.type === "video") {
          setFormData({
            ...baseData,
            videoUrl: topicData.videoUrl || "",
          });
        } else if (topicData.type === "mcq") {
          // Convert single mcq object to array format
          let mcqArray = [];
          if (topicData.mcq) {
            if (Array.isArray(topicData.mcq)) {
              mcqArray = topicData.mcq.map((q) => {
                const { text, code } = splitGoBlockFromText(q?.question);
                return {
                  question: text,
                  options: q?.options || ["", "", "", ""],
                  correctOption: q?.answer !== undefined ? q.answer : 0,
                  hasCode: Boolean(code),
                  code,
                };
              });
            } else {
              const { text, code } = splitGoBlockFromText(topicData.mcq.question || "");
              mcqArray = [{
                question: text,
                options: topicData.mcq.options || ["", "", "", ""],
                correctOption: topicData.mcq.answer !== undefined ? topicData.mcq.answer : 0,
                hasCode: Boolean(code),
                code,
              }];
            }
          } else {
            mcqArray = [{ question: "", options: ["", "", "", ""], correctOption: 0, hasCode: false, code: "" }];
          }

          setFormData({
            ...baseData,
            mcq: mcqArray,
          });
        } else if (topicData.type === "coding") {
          setFormData({
            ...baseData,
            question: topicData.codingProblem?.question || "",
            inputFormat: topicData.codingProblem?.inputFormat || "",
            outputFormat: topicData.codingProblem?.outputFormat || "",
            constraints: topicData.codingProblem?.constraints || [""],
            examples: topicData.codingProblem?.examples || [{ input: "", expected: "" }],
            difficulty: topicData.codingProblem?.difficulty || "easy",
            boilerplateCodes: topicData.codingProblem?.boilerplate || DEFAULT_BOILERPLATE_CODES,
            visibleTestCases: topicData.codingProblem?.testCases || [{ input: "", expected: "" }],
            hiddenTestCases: topicData.codingProblem?.hiddenTestCases || [{ input: "", expected: "" }],
          });
        }
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading topic:", err);
        if (isMounted) {
          errorToast("Failed to load topic");
          setIsLoading(false);
          // Don't navigate immediately - let user see error
        }
      }
    };

    loadTopic();
    return () => { isMounted = false; };
  }, [topicId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData || !formData.title || !formData.title.trim()) {
      newErrors.title = "Topic title is required";
    }

    if (formData && formData.type === "video" && !formData.videoUrl?.trim()) {
      newErrors.videoUrl = "Video URL is required";
    }

    if (formData && (formData.type === "mcq" || formData.type === "coding")) {
      const parsedPoints = Number.parseInt(formData.points, 10);
      if (formData.points === "" || Number.isNaN(parsedPoints) || parsedPoints < 0) {
        newErrors.point = "Points must be a non-negative integer";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const parsedPoints = Number.parseInt(formData.points, 10);

      const normalizeIoText = (value) =>
        String(value ?? "")
          .replace(/\r\n/g, "\n")
          .replace(/\\n/g, "\n");

      const buildMcqQuestion = (questionText, code, hasCode) => {
        const q = String(questionText ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trimEnd();
        const c = String(code ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trimEnd();
        if (!hasCode || !c.trim()) return q.trim();
        return `${q.trim()}\n\ngo:${c}:go`;
      };

      const topicData = {
        title: formData.title,
        type: formData.type,
        chapterId: formData.chapterId,
      };

      // Preserve topic ordering on edit.
      if (formData.orderNo !== undefined) {
        topicData.orderNo = formData.orderNo;
      }

      if (formData.type === "mcq" || formData.type === "coding") {
        topicData.points = Number.isNaN(parsedPoints) ? 0 : parsedPoints;
      }

      // Add type-specific data
      if (formData.type === "theory") {
        topicData.theoryContent = formData.theoryContent.filter((p) => p.trim());
      } else if (formData.type === "video") {
        topicData.videoUrl = formData.videoUrl;
      } else if (formData.type === "mcq") {
        // If single question, send as object; if multiple, send as array
        if (formData.mcq.length === 1) {
          const q = formData.mcq[0];
          topicData.mcq = {
            question: buildMcqQuestion(q.question, q.code, q.hasCode),
            options: q.options,
            answer: q.correctOption
          };
        } else {
          topicData.mcq = formData.mcq.map(q => ({
            question: buildMcqQuestion(q.question, q.code, q.hasCode),
            options: q.options,
            answer: q.correctOption
          }));
        }
      } else if (formData.type === "coding") {
        // Add IDs to test cases for backend execution tracking
        const testCasesWithIds = formData.visibleTestCases.map((tc, idx) => ({
          id: tc.id || `tc-${idx + 1}`,
          input: normalizeIoText(tc.input),
          expected: normalizeIoText(tc.expected),
        }));
        const hiddenTestCasesWithIds = formData.hiddenTestCases.map((tc, idx) => ({
          id: tc.id || `ht-${idx + 1}`,
          input: normalizeIoText(tc.input),
          expected: normalizeIoText(tc.expected),
        }));

        const normalizedExamples = (formData.examples || []).map((ex) => ({
          ...ex,
          input: normalizeIoText(ex?.input),
          expected: normalizeIoText(ex?.expected),
        }));

        topicData.codingProblem = {
          title: formData.title,
          question: formData.question,
          inputFormat: formData.inputFormat,
          outputFormat: formData.outputFormat,
          constraints: formData.constraints.filter((c) => c.trim()),
          examples: normalizedExamples,
          testCases: testCasesWithIds,
          hiddenTestCases: hiddenTestCasesWithIds,
          difficulty: formData.difficulty,
          boilerplate: formData.boilerplateCodes,
        };
      }

      await updateTopic(topicId, topicData, user?.token);
      successToast("Topic updated successfully!");
      navigate(`/topic/${topicId}`);
    } catch (err) {
      console.error("Error updating topic:", err);
      errorToast("Failed to update topic. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !formData) {
    return <SkeletonFormPage />;
  }

  return (
    <div className="min-h-screen">
      <SeoMeta
        title="Edit Topic - Code Arena Admin"
        description="Edit topic details"
        url={`https://www.code-arena.in/topics/edit/${topicId}`}
        image="/logo192.png"
      />

      <div className="ca-container max-w-4xl py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/topic/${topicId}`)}
          className="flex items-center gap-2 font-medium mb-6 transition-colors"
          style={{ color: 'var(--color-primary)' }}
        >
          <FaArrowLeft /> Back to Topic
        </button>

        {/* Form Card */}
        <div className="ca-card overflow-hidden">
          {/* Header */}
          <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} px-4 sm:px-8 py-5 sm:py-6`}>
            <h1 className="text-3xl font-bold text-white">Edit Topic</h1>
            <p className="text-white/80 mt-2">Update topic details</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-8">
            {/* Basic Info */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Topic Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`ca-input py-3 ${
                    errors.title
                      ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                      : ""
                  }`}
                />
                {errors.title && (
                  <p className="text-red-600 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              {(formData.type === "mcq" || formData.type === "coding") && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Points *
                  </label>
                  <input
                    type="number"
                    name="points"
                    value={formData.points}
                    onChange={handleInputChange}
                    placeholder={formData.type === "coding" ? "e.g., 80" : "e.g., 20"}
                    min="0"
                    step="1"
                    className={`ca-input py-3 ${
                      errors.point
                        ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                        : ""
                    }`}
                  />
                  {errors.point && (
                    <p className="text-red-600 text-sm mt-1">{errors.point}</p>
                  )}
                </div>
              )}

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>Type:</strong> {formData.type.toUpperCase()}
                </p>
              </div>
            </div>

            {/* Type-specific components */}
            {formData.type === "theory" && <EditTheoryTopic formData={formData} setFormData={setFormData} />}
            {formData.type === "video" && <EditVideoTopic formData={formData} setFormData={setFormData} errors={errors} />}
            {formData.type === "mcq" && <EditMCQTopic formData={formData} setFormData={setFormData} errors={errors} />}
            {formData.type === "coding" && <EditCodingTopic formData={formData} setFormData={setFormData} />}

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/topic/${topicId}`)}
                className="ca-btn-secondary flex-1 px-6 py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="ca-btn-primary flex-1 px-6 py-3"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
